import { useState } from 'react';
import { Button, Dialog, Field, Icon, Select } from '@dayon/design-system';
import { tooShort } from '../format.ts';
import { LIFT_REASONS, SELF_EXCLUSION_RULE } from '../vocabulary.ts';
import type { ControlRow } from '../types.ts';

/**
 * Take one control off.
 *
 * The row is not deleted — `active` becomes false and `lifted_at` is stamped —
 * so the account keeps both the day the control went on and the day it came
 * off. The reason and the note ride on the audit entry written in the same
 * transaction.
 *
 * PRESENTATIONAL. The page owns the request and the busy flag, because the
 * table's own Lift buttons read that flag too: a lift in flight has to disable
 * every other row's button, and a busy flag held inside this dialog could not
 * reach them.
 */
export function LiftDialog({
  open,
  onClose,
  target,
  busy,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  target: ControlRow | null;
  busy: boolean;
  onSubmit: (payload: { reasonCode: string; note: string }) => Promise<void>;
}) {
  const [reason, setReason] = useState<string>(LIFT_REASONS[0].code);
  const [note, setNote] = useState('');

  /* Re-seeded on each open so a note typed for one control cannot be submitted
     against another. Adjusted during render, not in an effect. */
  const [openedWith, setOpenedWith] = useState(open);
  if (openedWith !== open) {
    setOpenedWith(open);
    if (open) {
      setReason(LIFT_REASONS[0].code);
      setNote('');
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={target ? `Lift the ${target.label.toLowerCase()}?` : 'Lift the control'}
      dismissible={false}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={note.trim().length < 10 || busy}
            onClick={() => void onSubmit({ reasonCode: reason, note: note.trim() })}
          >
            {busy ? 'Lifting…' : 'Lift it'}
          </Button>
        </>
      }
    >
      <div className="stack">
        {/*
          WARN AND NOT DANGER, and the reason is the way back.

          The tone contract beside these two classes reads: warn is a reversible
          consequence or a limit about to be met, danger is the irreversible or
          anything that moves money or a reported figure. A lift is the first of
          those. Nothing is paid, nothing is debited and no reported figure
          changes when this is pressed — the control simply stops applying — and
          the control can be put straight back on from this same tab, as a new
          decision with its own reason code and note. What cannot be taken back
          is the RECORD of the lift, which is why the notice says so rather than
          implying the whole press can be undone.

          The nearest argument for danger is a withdrawal block, because taking
          it off un-holds money. It is still not this press that moves it: the
          payout path checks the controls in force when it pays, and paying is a
          separate decision on Player funds with its own confirmation and its
          own danger notice. If everything in this console were danger, nothing
          would be.

          WHAT THE NOTICE SAYS, AND WHERE EACH CLAIM COMES FROM. The status
          sentence is deliberately conditional, because the route restores the
          roster status only where three things hold: no control is left in
          force, the account still reads Restricted, and the last status move on
          file is the one an apply wrote. A player restricted by some other
          judgement is left exactly as an operator set them, so a flat "the
          account goes back to Active" would be a promise this cannot keep. The
          "nothing is sent" sentence is the same claim the Account controls
          panel already refuses to make: the lift handler writes no outbox
          message and no template covers one, so the player is not told. And
          "Apply a control" is the live button on the Account controls panel,
          gated on the same permission as the Lift button that opened this
          dialog — so whoever reads this notice can see it. If that button is
          ever renamed, this sentence is sending somebody to a label that no
          longer exists and has to be renamed with it.
        */}
        <div className="notice notice-warn">
          <Icon name="warning" />
          <div>
            The control stops applying the moment this is written, so whatever it was blocking is
            allowed again from here on &mdash; and only this one comes off, with anything else in
            force on the account left on. If this was the last control in force, and applying one is
            what moved the account to Restricted, the status goes back to what it was before.
            Nothing is sent to the player: they are not told the control came off, and they find out
            by trying. The way back is <strong>Apply a control</strong>, which puts it on again as a
            new decision with its own reason and note &mdash; this lift stays on the record either
            way.
          </div>
        </div>

        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            {/* The two columns behind this sentence are `active` and `lifted_at`, and neither is
                named on screen. An operator reads the State column and the date printed beside it,
                which is the same pair of facts under the names this console gives them; the column
                name would only say where the platform keeps them, which is nothing they can act
                on. */}
            The row is not deleted — the control reads Off from here on, with today shown beside it
            as the day it was lifted, so the account keeps both the day the control went on and the
            day it came off. The reason and note below ride on the audit entry written in the same
            transaction.
            {target?.record?.applied_by === 'PLAYER' ? ` ${SELF_EXCLUSION_RULE}` : ''}
          </div>
        </div>

        <Field label="Reason" hint="Closed set, stored with the decision.">
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {LIFT_REASONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Note"
          error={tooShort(note)}
          hint="At least 10 characters. Taking a control off is a decision too, and the audit entry carries this sentence."
        >
          {({ id, describedBy, invalid }) => (
            <textarea
              id={id}
              className="input"
              rows={3}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Risk review closed with no finding; the block is no longer warranted."
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
